import DeleteIcon from "@mui/icons-material/Delete";
import { Alert, Box, Button } from "@mui/material";
import { Stack } from "@mui/system";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { z } from "zod";
import WithConfirmationButton from "../../atoms/WithConfirmationButton";
import CustomDialog from "../CustomDialog";
import { useForm, useWatch } from "react-hook-form";
import { formComponents } from "./fields";
import ModelInfoButton from "../components/ModelInfoButton";

export const CustomFormFields = (props) => {
  const { fields, control } = {
    ...useFormContext(),
    ...props,
  };

  return fields
    .filter((f) => !f.hidden)
    .map((field, index) => {
      let Component =
        field.formComponent ||
        formComponents[field.type]?.Component ||
        formComponents["default"].Component;
      return <Component key={index} field={field} control={control} />;
    });
};

export const CustomFormTitle = (props) => {
  const { title, errors, modelName } = {
    ...useFormContext(),
    ...props,
  };

  return (
    <Stack direction="row" alignItems="center" flexGrow={1}>
      <span>
        {title || "Form"}
        {Object.keys(errors).length ? " (errors)" : ""}
      </span>
      <Box ml="auto">
        <ModelInfoButton modelName={modelName} />
      </Box>
    </Stack>
  );
};

export const CustomFormErrors = (props) => {
  const { errors } = {
    ...useFormContext(),
    ...props,
  };

  return (
    errors.root && (
      <Alert severity="error">
        {errors.root.message || "An error occurred during submission."}
      </Alert>
    )
  );
};

export const CustomFormContent = ({ children, ...props }) => {
  const { fields, control, errors } = {
    ...useFormContext(),
    ...props,
  };
  return (
    <Stack spacing={2}>
      {children || (
        <>
          <CustomFormErrors />
          <CustomFormFields />
        </>
      )}
    </Stack>
  );
};

export const CustomFormDataWatcher = ({ children, ...props }) => {
  const { control } = {
    ...useFormContext(),
    ...props,
  };
  const data = useWatch({ control });
  return children(data);
};

export const CustomFormActions = ({ children, ...props }) => {
  const { handleDelete, onCancel, submitting, deleting } = {
    ...useFormContext(),
    ...props,
  };

  return (
    children || (
      <>
        {handleDelete && (
          <WithConfirmationButton
            confirmationMessage="Delete item ?"
            endIcon={<DeleteIcon sx={{ scale: "0.8" }} />}
            onClick={handleDelete}
            loading={deleting}
            disabled={deleting}
            color="error"
            sx={{ mr: "auto" }}
          >
            Delete
          </WithConfirmationButton>
        )}

        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" type="submit" loading={submitting}>
          Submit
        </Button>
      </>
    )
  );
};

export const FormContext = createContext(undefined);
export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("useFormContext must be used within a CustomForm");
  }
  return context;
};

const CustomForm = ({
  title,
  fields,
  defaultValues = {},
  submitter,
  deleter,
  onDone,
  onDeleted,
  onError,
  onCancel,
  children,
  onIsDirty,
  errors: parentErrors,
  modelName, // only for debug. All necessary information should be in fields
}) => {
  if (!submitter) console.error("No submitter function provided for the form.");

  const {
    register,
    handleSubmit: handleSubmitRHF,
    control,
    setError,
    clearErrors,
    formState: { errors },
    getValues,
    watch,
  } = useForm({
    defaultValues: {
      ...defaultValues,
    },
  });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const schema = useMemo(() => {
    return z.object(
      Object.fromEntries(
        fields
          .filter((field) => !field.disabled && field.schema)
          .map((field) => [field.name, field.schema]),
      ),
    );
  });

  const handleServerErrors = (errors) => {
    console.error("Error during form submission:", errors);
    const { non_field_error, ...fieldErrors } = errors.response?.data || {};
    setError("root", { message: non_field_error || "Submission failed" });
    Object.entries(fieldErrors).forEach(([name, error]) => {
      setError(name, { message: error || "Invalid value" });
    });
  };

  const handleSubmit = useCallback(
    (formData) => {
      const parsed = schema.safeParse(formData);
      if (!parsed.success) {
        //setErrors(parsed.error.flatten());
        console.error("Form validation failed:", parsed.error);
        return;
      }
      if (!submitter) {
        console.error("No submitter function provided for the form.");
        return;
      }
      setSubmitting(true);
      submitter(
        { id: formData.id, data: formData },
        {
          onSuccess: (data) => {
            clearErrors();
            onDone && onDone(data);
            setSubmitting(false);
          },
          onError: (errors) => {
            handleServerErrors(errors);
            setSubmitting(false);
          },
        },
      );
    },
    [fields, schema, submitter, onDone, onError],
  );

  const handleDelete =
    deleter &&
    useCallback(() => {
      if (!deleter) {
        console.error("No deleter function provided for the form.");
        return;
      }
      setDeleting(true);
      deleter(defaultValues.id, {
        onSuccess: (data) => {
          onDeleted && onDeleted(data);
          setDeleting(false);
        },
        onError: (error) => {
          console.error("Error during deletion:", error);
          onError && onError(error);
          setDeleting(false);
        },
      });
    }, [deleter, onDeleted, onError, defaultValues.id]);

  useEffect(() => {
    if (parentErrors) {
      Object.entries(parentErrors).forEach(([name, error]) => {
        setError(name, { message: error || "Invalid value" });
      });
    }
  }, [parentErrors, setError]);

  return (
    <FormContext.Provider
      value={{
        register,
        fields,
        onIsDirty,
        control,
        modelName,
        errors,
        title,
        handleDelete,
        onCancel,
        submitting,
        deleting,
        getValues,
        watch,
      }}
    >
      <form
        onSubmit={handleSubmitRHF(handleSubmit)}
        style={{ margin: 0, padding: 0 }}
        encType="multipart/form-data"
      >
        {children || (
          <>
            <CustomDialog title>
              <CustomFormTitle />
            </CustomDialog>
            <CustomDialog content>
              <CustomFormContent />
            </CustomDialog>
            <CustomDialog actions>
              <CustomFormActions />
            </CustomDialog>
          </>
        )}
      </form>
    </FormContext.Provider>
  );
};

export default CustomForm;
